'use client';
import { useContext } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import {
  suggestionButtonVariants,
  suggestionItemVariants,
} from '../_assets/animation.variants.const';
import RingsFilled from '../_assets/rings-filled.svg';
import Rings from '../_assets/rings.svg';

import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { PromptSuggestions } from '@/constant/Constants';
import { useExplorer } from '@/hooks/useExplorer';
import { explorerAnalytics } from '@/lib/analytics';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { CircleArrowUpIcon, CircleXIcon } from 'lucide-react';
import SDKSelector from './SDKSelector';

/**
 * Renders AI Prompt text box on homepage
 */
interface ExplorerChatBoxProps {
  height?: string;
  handleChatSubmit: (prompt: string, sdk?: string | null) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onClear?: () => void;
}

const ExplorerChatBox = ({
  handleChatSubmit,
  label,
  description,
  placeholder,
  height,
  textareaRef,
  onClear,
}: ExplorerChatBoxProps) => {
  return (
    <div className="relative">
      {(label || description) && (
        <div className={`flex px-[1.1875rem] py-3 gap-2 font-sora`}>
          <h2
            className={`bg-gradient-to-r font-semibold from-shade-red-500 to-shade-red-800 bg-clip-text text-transparent text-[1rem] leading-tight`}
          >
            {label || ''}
          </h2>
          <p className="m-0 leading-tight text-[1rem] text-[#012A38B2]">
            {description || ''}
          </p>
        </div>
      )}

      <Textarea
        onClear={onClear}
        onSubmit={handleChatSubmit}
        placeholder={placeholder}
        height={height}
        textareaRef={textareaRef}
      />
    </div>
  );
};

/**
 * Suggestion Button under AI Prompt box upon entering query
 */
function SuggestionButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      className={twMerge(
        "before:bg-gradient-to-r from-shade-red-500 to-shade-red-800 before:absolute before:inset-0 before:rounded-[inherit] before:p-[2px] before:content-[''] before:mask-custom rounded-[1.25rem] bg-[rgba(255,255,255,0.52)] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] backdrop-blur-0 font-sora flex items-start p-4 gap-[0.375rem] text-left text-balance font-medium text-[0.75rem] hover:shadow-lg hover:bg-[rgba(255,255,255,0.7)] hover:scale-[1.02] transition-all duration-300 ease-in-out",
        className
      )}
      type="button"
    >
      <Image src={RingsFilled} alt="suggestion" />
      <span
        className={`bg-gradient-to-r from-shade-red-500 to-shade-red-800 bg-clip-text text-transparent leading-tight font-medium`}
      >
        {children}
      </span>
    </button>
  );
}

/**
 * Actual Textarea component which renders inside ExplorerChatBox
 */
interface TextareaProps {
  placeholder?: string;
  height?: string;
  onClear?: () => void;
  onSubmit: (prompt: string, sdk?: string | null) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

function Textarea({
  onClear,
  onSubmit,
  placeholder,
  height,
  textareaRef,
}: TextareaProps) {
  const { selectedSDK, setSelectedSDK, userPrompt, setUserPrompt } =
    useExplorer();
  const { isLoggedIn } = useContext(ClientAuthContext);
  const { isGenerating } = useCodeGenerationStore();

  const handleUserPromptChange = (value: string) => {
    setUserPrompt(value);
  };

  function handleKeyDown(
    e:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    // Don't trigger submit if text is still generating
    if (isGenerating) {
      e.preventDefault();
      return;
    }

    if (
      ('key' in e &&
        e.key === 'Enter' &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey) ||
      ('button' in e && e.button === 0)
    ) {
      e.preventDefault();
      onSubmit(userPrompt, selectedSDK);
    }
  }

  const handleSuggestionClick = (prompt: string, index: number) => {
    explorerAnalytics.aiSuggestionClicked({
      suggestionText: prompt,
      suggestionPosition: index + 1,
      sdkLanguage: selectedSDK,
      isLoggedIn: !!isLoggedIn,
    });
    setUserPrompt(prompt);
    onSubmit(prompt, selectedSDK);
  };

  const handleSelectSDK = (value: string) => {
    setSelectedSDK(value);
  };

  const handleClear = () => {
    onClear?.();
    setUserPrompt('');
  };

  return (
    <>
      <motion.div
        initial={false}
        className={clsx(
          `backdrop-blur-[22px] shadow-[0px_4px_19px_rgba(0,0,0,0.25),_0_0_12px_hsl(0_0%_100%)_inset] bg-[linear-gradient(0deg,rgba(255,255,255,0.5)_0%,rgba(255,255,255,0.5)_100%),linear-gradient(0deg,rgba(211,217,220,0)_0%,rgba(211,217,220,0)_100%)] flex rounded-[1.25rem] py-[1.25rem] px-[1.1875rem] items-baseline gap-3 font-sora origin-top transition-all duration-300 ease-in-out hover:shadow-[0px_8px_24px_rgba(0,0,0,0.3),_0_0_15px_hsl(0_0%_100%)_inset] hover:backdrop-blur-[26px] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.6)_0%,rgba(255,255,255,0.6)_100%),linear-gradient(0deg,rgba(211,217,220,0)_0%,rgba(211,217,220,0)_100%)] `,
          userPrompt &&
            "relative before:content-[''] before:absolute before:inset-0 before:rounded-[inherit] before:z-[-1] before:p-[2px] before:bg-[linear-gradient(90deg,#e93406_0%,#831d04_100%)] before:mask-custom"
        )}
        animate={{
          height: height ? height : userPrompt ? '16rem' : '12rem',
        }}
        transition={{
          duration: 0.1, // Slightly faster
          ease: [0.4, 0.0, 0.2, 1], // Custom easing for smoother expansion
        }}
      >
        <Image src={Rings} alt="your business challenge" />
        <textarea
          className={`bg-blue-400 pr-8 flex-1 font-medium self-stretch placeholder:text-[#012A38] placeholder:opacity-50 text-[0.875rem] appearance-none resize-none bg-transparent focus:outline-none`}
          value={userPrompt}
          onChange={(e) => handleUserPromptChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || ''}
          ref={textareaRef}
        />
        {userPrompt && (
          <AnimatePresence mode="wait">
            <motion.div
              key="search-submit"
              initial="hidden"
              animate="show"
              variants={suggestionButtonVariants}
              className="flex gap-2 absolute bottom-4 right-4 items-end"
            >
              <SDKSelector
                selectedSDK={selectedSDK}
                onSelectSDK={handleSelectSDK}
                disabled={userPrompt.length === 0 || isGenerating}
              />
              <button
                onClick={(e) => handleKeyDown(e)}
                className={`inset-0 rounded-[1.25rem] bg-gradient-to-b from-white/20 to-white/20`}
              >
                <CircleArrowUpIcon className="size-5" />
              </button>
            </motion.div>

            <motion.div
              key="search-clear"
              initial="hidden"
              animate="show"
              variants={suggestionItemVariants}
              className="flex gap-2 absolute top-4 right-4 items-end"
            >
              <button
                onClick={handleClear}
                className={`bg-neutral-200 rounded-full p-2 flex items-center gap-2 leading-tight font-medium text-[0.75rem] text-left font-sora`}
              >
                <CircleXIcon className="size-5" />
              </button>
            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>

      <AnimatePresence mode="wait">
        {!userPrompt ? (
          <motion.div
            key="suggestions"
            initial="hidden"
            animate="show"
            exit="exit"
            variants={suggestionButtonVariants}
            className={`rounded-b-[2.5rem] bg-[linear-gradient(0deg,rgba(1,42,56,0.1)_0%,rgba(1,42,56,0.1)_100%),linear-gradient(0deg,rgba(211,217,220,0)_0%,rgba(211,217,220,0)_100%)] pt-5 pb-5 px-6 mx-5 before:mask-custom`}
          >
            <div className="flex gap-2">
              {PromptSuggestions?.length > 0 &&
                PromptSuggestions.map((suggestion, index) => (
                  <motion.div
                    key={index}
                    variants={suggestionItemVariants}
                    onClick={() => handleSuggestionClick(suggestion, index)}
                    className="flex items-center gap-2"
                  >
                    <SuggestionButton>{suggestion}</SuggestionButton>
                  </motion.div>
                ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="search-submit"
            initial="hidden"
            animate="show"
            exit="exit"
            variants={suggestionButtonVariants}
            className="flex items-center justify-center gap-2 text-center mt-3 text-[1rem] text-[#012A38B2]"
          >
            <span>
              AI can make mistakes. Check important info. Do not share sensitive
              details like API keys here.
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ExplorerChatBox;
